/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Comandero — Sales reports (PURE LOGIC, DETERMINISTIC, NO AI)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * CC-6: this file is PURE `.ts`. Does NOT import lingui macros (`msg`/`t`) or
 * no UI. Takes flat rows (orders, lines, payments) and returns aggregated
 * numbers. All amounts are integers in cents.
 *
 * Pure statistics: same inputs produce ALWAYS same output (no
 * "now" dates, without randomness). Date range is decided by caller on
 * filtering passed rows.
 */

export interface ReportOrder {
  id: string;
  status: string | null;
  total: number | null;
  /** Employee to whom sale is attributed (who charged). */
  employeeId: string | null;
  /** ISO charge/close timestamp for hourly grouping (closed_at or paid_at). */
  closedAt: string | null;
}

export interface ReportLine {
  orderId: string;
  productId: string | null;
  name: string | null;
  qty: number | null;
  lineTotal: number | null;
  /** Product category (optional, to group sales by category). */
  categoryId?: string | null;
  categoryName?: string | null;
}

export interface ReportPayment {
  orderId: string;
  amount: number | null;
  tip: number | null;
  employeeId: string | null;
  /** Payment method (cash, card, ...), to group by method. */
  method?: string | null;
}

export interface ProductSales {
  productId: string;
  name: string;
  qty: number;
  total: number;
}

export interface EmployeeSales {
  employeeId: string;
  orders: number;
  total: number;
  tips: number;
}

export interface HourlySales {
  /** Hora local 0..23. */
  hour: number;
  orders: number;
  total: number;
}

export interface CategorySales {
  categoryId: string;
  name: string;
  qty: number;
  total: number;
}

export interface PaymentMethodSales {
  method: string;
  count: number;
  total: number;
}

export interface DailySales {
  /** Fecha local YYYY-MM-DD. */
  day: string;
  orders: number;
  total: number;
}

export interface SalesSummary {
  /** Invoiced total of paid orders, in cents. */
  grossSales: number;
  /** Number of tickets (paid orders). */
  ticketCount: number;
  /** Ticket medio (gross / tickets), centavos, truncado. */
  averageTicket: number;
  /** Suma de propinas, en centavos. */
  tips: number;
  /** Voided/cancelled orders (void status). */
  voidCount: number;
}

const PAID = "paid";
const VOID = "void";

function isPaid(o: ReportOrder): boolean {
  return o.status === PAID;
}

/** Resumen global: ventas, nº de tickets, ticket medio, propinas, voids. */
export function summarize(orders: ReportOrder[], payments: ReportPayment[]): SalesSummary {
  const paid = orders.filter(isPaid);
  const grossSales = paid.reduce((acc, o) => acc + Math.max(0, o.total ?? 0), 0);
  const ticketCount = paid.length;
  const paidIds = new Set(paid.map((o) => o.id));
  const tips = payments
    .filter((p) => paidIds.has(p.orderId))
    .reduce((acc, p) => acc + Math.max(0, p.tip ?? 0), 0);
  const voidCount = orders.filter((o) => o.status === VOID).length;
  const averageTicket = ticketCount > 0 ? Math.trunc(grossSales / ticketCount) : 0;
  return { grossSales, ticketCount, averageTicket, tips, voidCount };
}

/** Sales by product (paid orders only), ordered by total desc. */
export function salesByProduct(orders: ReportOrder[], lines: ReportLine[]): ProductSales[] {
  const paidIds = new Set(orders.filter(isPaid).map((o) => o.id));
  const map = new Map<string, ProductSales>();
  for (const l of lines) {
    if (!l.productId || !paidIds.has(l.orderId)) {
      continue;
    }
    const entry = map.get(l.productId) ?? {
      productId: l.productId,
      name: l.name ?? "",
      qty: 0,
      total: 0,
    };
    entry.qty += Math.max(0, l.qty ?? 0);
    entry.total += Math.max(0, l.lineTotal ?? 0);
    if (!entry.name && l.name) {
      entry.name = l.name;
    }
    map.set(l.productId, entry);
  }
  return [...map.values()].toSorted((a, b) => b.total - a.total || a.name.localeCompare(b.name));
}

/**
 * Sales by employee (server/cashier who charged). Attributes paid order to their
 * `employeeId`; tips are summed from payments of that order.
 */
export function salesByEmployee(orders: ReportOrder[], payments: ReportPayment[]): EmployeeSales[] {
  const paid = orders.filter(isPaid);
  const map = new Map<string, EmployeeSales>();
  const tipByOrder = new Map<string, number>();
  for (const p of payments) {
    tipByOrder.set(p.orderId, (tipByOrder.get(p.orderId) ?? 0) + Math.max(0, p.tip ?? 0));
  }
  for (const o of paid) {
    const key = o.employeeId ?? "unassigned";
    const entry = map.get(key) ?? { employeeId: key, orders: 0, total: 0, tips: 0 };
    entry.orders += 1;
    entry.total += Math.max(0, o.total ?? 0);
    entry.tips += tipByOrder.get(o.id) ?? 0;
    map.set(key, entry);
  }
  return [...map.values()].toSorted((a, b) => b.total - a.total);
}

/**
 * Local hour (0..23) of ISO timestamp. Deterministic: derives hour from
 * local components of date (without "now"). Returns null if not parsing.
 */
export function hourOf(iso: string | null): number | null {
  if (!iso) {
    return null;
  }
  const d = new Date(iso);
  const h = d.getHours();
  return Number.isNaN(h) ? null : h;
}

/** Sales by hour of day (paid only). Returns only hours with activity. */
export function salesByHour(orders: ReportOrder[]): HourlySales[] {
  const map = new Map<number, HourlySales>();
  for (const o of orders.filter(isPaid)) {
    const h = hourOf(o.closedAt);
    if (h === null) {
      continue;
    }
    const entry = map.get(h) ?? { hour: h, orders: 0, total: 0 };
    entry.orders += 1;
    entry.total += Math.max(0, o.total ?? 0);
    map.set(h, entry);
  }
  return [...map.values()].toSorted((a, b) => a.hour - b.hour);
}

const UNCATEGORIZED = "uncategorized";

/**
 * Sales by product category (paid orders only). Lines without
 * `categoryId` grouped into "uncategorized". Sorted by total desc.
 */
export function salesByCategory(orders: ReportOrder[], lines: ReportLine[]): CategorySales[] {
  const paidIds = new Set(orders.filter(isPaid).map((o) => o.id));
  const map = new Map<string, CategorySales>();
  for (const l of lines) {
    if (!paidIds.has(l.orderId)) {
      continue;
    }
    const key = l.categoryId ?? UNCATEGORIZED;
    const entry = map.get(key) ?? {
      categoryId: key,
      name: l.categoryName ?? "",
      qty: 0,
      total: 0,
    };
    entry.qty += Math.max(0, l.qty ?? 0);
    entry.total += Math.max(0, l.lineTotal ?? 0);
    if (!entry.name && l.categoryName) {
      entry.name = l.categoryName;
    }
    map.set(key, entry);
  }
  return [...map.values()].toSorted((a, b) => b.total - a.total || a.name.localeCompare(b.name));
}

/**
 * Sales by payment method (only paid orders). Sums charged
 * amount (without tip) per method. Ordered by total desc.
 */
export function salesByPaymentMethod(
  orders: ReportOrder[],
  payments: ReportPayment[],
): PaymentMethodSales[] {
  const paidIds = new Set(orders.filter(isPaid).map((o) => o.id));
  const map = new Map<string, PaymentMethodSales>();
  for (const p of payments) {
    if (!paidIds.has(p.orderId)) {
      continue;
    }
    const key = p.method ?? UNCATEGORIZED;
    const entry = map.get(key) ?? { method: key, count: 0, total: 0 };
    entry.count += 1;
    entry.total += Math.max(0, p.amount ?? 0);
    map.set(key, entry);
  }
  return [...map.values()].toSorted((a, b) => b.total - a.total);
}

/**
 * Local YYYY-MM-DD date from ISO timestamp. Deterministic. Null if parse fails.
 */
export function dayOf(iso: string | null): string | null {
  if (!iso) {
    return null;
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Sales per day (paid only). Returns days with activity, asc by date. */
export function salesByDay(orders: ReportOrder[]): DailySales[] {
  const map = new Map<string, DailySales>();
  for (const o of orders.filter(isPaid)) {
    const day = dayOf(o.closedAt);
    if (day === null) {
      continue;
    }
    const entry = map.get(day) ?? { day, orders: 0, total: 0 };
    entry.orders += 1;
    entry.total += Math.max(0, o.total ?? 0);
    map.set(day, entry);
  }
  return [...map.values()].toSorted((a, b) => a.day.localeCompare(b.day));
}
